import type {
  CreateOrganizationRequest,
  InviteFirstOrgAdminRequest,
  InviteFirstOrgAdminResponse,
  ListInvitationsResponse,
  ListOrganizationsResponse,
  LogoUploadUrlRequest,
  LogoUploadUrlResponse,
  OrganizationResponse,
  OrgBrandingResponse,
  ResendInvitationResponse,
  SmsTemplatesResponse,
  UpdateOrganizationDto,
  UpdateOrgBrandingRequest,
  UpsertSmsTemplatesRequest,
} from '@jq4race/shared';

import { request } from './client.js';

export async function listOrganizations(): Promise<ListOrganizationsResponse> {
  return request<ListOrganizationsResponse>('/super-admin/organizations', { method: 'GET' });
}

export async function createOrganization(
  input: CreateOrganizationRequest,
): Promise<OrganizationResponse> {
  return request<OrganizationResponse>('/super-admin/organizations', {
    method: 'POST',
    body: input,
  });
}

export async function inviteFirstOrgAdmin(
  organizationId: string,
  input: InviteFirstOrgAdminRequest,
): Promise<InviteFirstOrgAdminResponse> {
  return request<InviteFirstOrgAdminResponse>(
    `/super-admin/organizations/${encodeURIComponent(organizationId)}/invitations`,
    {
      method: 'POST',
      body: input,
    },
  );
}

// ── Org Branding — JQ4-13 ────────────────────────────────────────────────────

export async function getOrgBranding(): Promise<OrgBrandingResponse> {
  return request<OrgBrandingResponse>('/admin/organization/branding', { method: 'GET' });
}

export async function requestLogoUploadUrl(
  contentType: LogoUploadUrlRequest['contentType'],
): Promise<LogoUploadUrlResponse> {
  return request<LogoUploadUrlResponse>('/admin/organization/branding/logo-upload-url', {
    method: 'POST',
    body: { contentType } satisfies LogoUploadUrlRequest,
  });
}

export async function updateOrgBranding(
  body: UpdateOrgBrandingRequest,
): Promise<OrgBrandingResponse> {
  return request<OrgBrandingResponse>('/admin/organization/branding', {
    method: 'PATCH',
    body,
  });
}

// ── SMS Templates — JQ4-34 ───────────────────────────────────────────────────

/** GET /admin/organization/sms-templates — returns current templates and isCustom flag. */
export async function getSmsTemplates(): Promise<SmsTemplatesResponse> {
  return request<SmsTemplatesResponse>('/admin/organization/sms-templates', { method: 'GET' });
}

/** PUT /admin/organization/sms-templates — upserts custom EN + ES templates. */
export async function upsertSmsTemplates(
  body: UpsertSmsTemplatesRequest,
): Promise<SmsTemplatesResponse> {
  return request<SmsTemplatesResponse>('/admin/organization/sms-templates', {
    method: 'PUT',
    body,
  });
}

/** DELETE /admin/organization/sms-templates — restores JQRACE4 default (clears both columns). */
export async function deleteSmsTemplates(): Promise<SmsTemplatesResponse> {
  return request<SmsTemplatesResponse>('/admin/organization/sms-templates', { method: 'DELETE' });
}

// ── JQ4-48 — Edit org core fields ────────────────────────────────────────────

/** GET /super-admin/organizations/:orgId — returns a single org for form pre-population. */
export async function getOrganization(orgId: string): Promise<OrganizationResponse> {
  return request<OrganizationResponse>(`/super-admin/organizations/${encodeURIComponent(orgId)}`, {
    method: 'GET',
  });
}

/** PATCH /super-admin/organizations/:orgId — partially updates core fields of an org. */
export async function updateOrganization(
  orgId: string,
  body: UpdateOrganizationDto,
): Promise<OrganizationResponse> {
  return request<OrganizationResponse>(`/super-admin/organizations/${encodeURIComponent(orgId)}`, {
    method: 'PATCH',
    body,
  });
}

// ── Super Admin org_admin invitation management — JQ4-47/JQ4-46 ─────────────

/** GET /super-admin/organizations/:orgId/invitations — lists all invitations for an org. */
export async function listOrganizationInvitations(orgId: string): Promise<ListInvitationsResponse> {
  return request<ListInvitationsResponse>(
    `/super-admin/organizations/${encodeURIComponent(orgId)}/invitations`,
    { method: 'GET' },
  );
}

/** POST /super-admin/organizations/:orgId/invitations/:invId/resend — resends an org_admin invitation. */
export async function resendOrganizationInvitation(
  orgId: string,
  invitationId: string,
): Promise<ResendInvitationResponse> {
  return request<ResendInvitationResponse>(
    `/super-admin/organizations/${encodeURIComponent(orgId)}/invitations/${encodeURIComponent(invitationId)}/resend`,
    { method: 'POST' },
  );
}
